import {
  Autocomplete,
  Chip,
  DialogContent,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { Formik } from "formik";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import Checkbox from "@mui/material/Checkbox";
import { generateNumbers } from "services/service";
import moment from "moment-timezone";
import { useMaterialUIController } from "context";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createRate } from "store/slice/rates/rateSlice";
import CircularIndeterminate from "components/MDLoading";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { createRateValidation } from "services/validation";
import MDDropDown from "components/MDDropDown";
import { getRatesByPlaceId } from "store/slice/rates/rateSlice";
import { DateRangePicker } from "rsuite";
import CustomTooltip from "components/Common/CustomTooltip";
import { updatePlace, getPlaces } from "store/slice/places/placeSlice";
import { triggerSetupSnackbar } from "store/slice/globalControl/globalControlSlice";
import { useNavigate } from "react-router-dom";

const tabInfo = {
  minimumAmount:
    "Monthly subscriptions purchased through this rate renew on the first of each month. After the first, the rate is prorated daily until it reaches the set Minimum Amount. Once this minimum is met, the rate remains at that amount until the beginning of the next month.",
  parentRate:
    "A Parent Rate allows you to group multiple rates under a single display name. When customers select the Parent Rate, they will be presented with the various stored rate options to choose from.",
  freeRate: "Create a Free Rate that can be assigned to any user",
  permitRate:
    "A Permit Rate allows customers to request a free permit. Permit requests can be reviewed and managed on the Permits tab. Ensure that all Permit Types are created on the Permit Dashboard before making a Permit Rate live.",
  isPass: "Create shuttle ticket rate scenario",
  createRateValidateLater:
    "A Validate Later rate instructs customers to visit a specific location- such as a restaurant- to obtain a validation code. Customers are granted a grace period to enter this code before being marked as a violator. Validation codes are created in the Validation tab",
  payNowValidateLater:
    "Pay Now Validate Later Rates charge the customer the full amount of the parking session upfront. Customers are then directed to a location to receive a validation code. If a code is entered before the grace period expires, the customer receives free or discounted parking. If no code is entered, the full payment is retained. Validation codes are created in the Validation tab.",
  validationCode:
    "Require a validation code to apply or access this rate, typically for discounts or restrictions.",
  isCustomSubscription:
    "Monthly subscriptions purchased through this rate renew every 30 days unless canceled or otherwise terminated. Customers have the option to deselect ' auto- renew' at the time of purchase.",
};

const CreateRates = (props) => {
  const {
    dialogOpen,
    onClose,
    placeId,
    ratesData,
    placeList,
    isGroupedLocation,
    placeData,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [isEndDayTimePickerOpen, setEndDayTimePickerOpen] = useState(false);
  const [isGraceEndDayTimePickerOpen, setGraceEndDayTimePickerOpen] =
    useState(false);
  const [isStartDayTimePickerOpen, setStartDayTimePickerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [openTooltipId, setOpenTooltipId] = useState(null);

  // ✅ Only emit guide events while guide is actively running
  const isGuideActive = useSelector(
    (state) => state.ui?.guide?.active ?? false,
  );
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  const handleRateSuccess = () => {
    // Fire guide event — GuideOverlay checks transit/active state, so it's safe to always dispatch
    window.dispatchEvent(
      new CustomEvent("GUIDE_STEP_COMPLETE", {
        detail: { step: "CREATE_RATE" },
      }),
    );
  };

  const handleCreateRate = (rateData) => {
    setIsLoading(true);
    dispatch(createRate(rateData))
      .unwrap()
      .then(async (res) => {
        const success = res?.success;
        setIsLoading(false);
        setNotification({
          ...notification,
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message,
          icon: success ? "check" : "warning",
          show: true,
        });
        if (success) {
          const currentSetup = placeData?.setup || {};
          const currentSteps = currentSetup?.steps || {};
          const payload = {
            setup: {
              ...currentSetup,
              steps: {
                ...currentSteps,
                ratesCreate: true,
              },
            },
          };
          await dispatch(getRatesByPlaceId(placeId));
          await dispatch(
            updatePlace({
              placeId,
              type: "UPDATE",
              ...payload,
            }),
          ).unwrap();
          // ✅ Refresh placesList + selectedPlace so checklist shows ratesCreate:true
          await dispatch(getPlaces());
          onClose();
          handleRateSuccess();
          // Use the saved payload.setup (not stale placeData.setup)
          dispatch(triggerSetupSnackbar(payload?.setup));
        }
      })
      .catch((err) => {
        console.error("Error creating rate:", err);
        setIsLoading(false);
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: err?.message,
          icon: "warning",
          show: true,
        });
      });
  };

  const startDayOptions = [
    { label: "Same Day" },
    { label: "Next Day" },
    { label: "Day after Tomorrow" },
  ];

  const endDayOptions = {
    Custom: [
      { label: "Sunday" },
      { label: "Monday" },
      { label: "Tuesday" },
      { label: "Wednesday" },
      { label: "Thursday" },
      { label: "Friday" },
      { label: "Saturday" },
      { label: "Same Day" },
      { label: "Next Day" },
      { label: "Day After Tomorrow" },
      { label: "3rd Day From Today" },
      { label: "4th Day From Today" },
      { label: "5th Day From Today" },
      { label: "6th Day From Today" },
      { label: "7th Day From Today" },
      { label: "8th Day From Today" },
      { label: "9th Day From Today" },
      { label: "10th Day From Today" },
    ],
    Overnight: [{ label: "Next Day" }, { label: "Same Day" }],
  };

  const hoursOptions = [
    { label: "15 MINUTE" },
    { label: "30 MINUTE" },
    { label: "45 MINUTE" },
    { label: "90 MINUTE" },
    { label: "1 HOUR" },
    { label: "2 HOUR" },
    { label: "3 HOUR" },
    { label: "4 HOUR" },
    { label: "5 HOUR" },
    { label: "6 HOUR" },
    { label: "7 HOUR" },
    { label: "8 HOUR" },
    { label: "9 HOUR" },
    { label: "10 HOUR" },
    { label: "11 HOUR" },
    { label: "12 HOUR" },
    { label: "13 HOUR" },
    { label: "14 HOUR" },
    { label: "15 HOUR" },
    { label: "16 HOUR" },
    { label: "17 HOUR" },
    { label: "18 HOUR" },
    { label: "19 HOUR" },
    { label: "20 HOUR" },
    { label: "21 HOUR" },
    { label: "22 HOUR" },
    { label: "23 HOUR" },
    { label: "24 HOUR" },
  ];
  const hoursOptionsForFreeParking = [
    { label: "1 HOUR" },
    { label: "2 HOUR" },
    { label: "3 HOUR" },
    { label: "4 HOUR" },
    { label: "5 HOUR" },
    { label: "6 HOUR" },
    { label: "7 HOUR" },
    { label: "8 HOUR" },
    { label: "9 HOUR" },
    { label: "10 HOUR" },
  ];
  const gracePeriodOptions = [
    { label: "10 MINUTES" },
    { label: "15 MINUTES" },
    { label: "20 MINUTES" },
    { label: "25 MINUTES" },
    { label: "30 MINUTES" },
    { label: "35 MINUTES" },
    { label: "40 MINUTES" },
    { label: "45 MINUTES" },
    { label: "50 MINUTES" },
    { label: "55 MINUTES" },
    { label: "60 MINUTES" },
    { label: "90 MINUTES" },
    { label: "120 MINUTES" },
    { label: "240 MINUTES" },
    { label: "360 MINUTES" },
    { label: "480 MINUTES" },
  ];

  const gracePeriodTypeOptions = [
    { label: "Minutes", value: "minutes" },
    { label: "Hours", value: "hours" },
    { label: "Days", value: "days" },
  ];

  const permitGracePeriodOptions = {
    minutes: Array.from({ length: 11 }, (_, i) => {
      const minutes = (i + 2) * 5;
      return {
        label: `${minutes} ${minutes === 1 ? "Minute" : "Minutes"}`,
        value: minutes,
      };
    }),

    hours: Array.from({ length: 24 }, (_, i) => {
      const hours = i + 1;
      return {
        label: `${hours} ${hours === 1 ? "Hour" : "Hours"}`,
        value: hours * 60,
      };
    }),

    days: Array.from({ length: 5 }, (_, i) => {
      const days = i + 1;
      return {
        label: `${days} ${days === 1 ? "Day" : "Days"}`,
        value: days * 24 * 60,
      };
    }),
  };
  return (
    <>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Creating Place "
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDDialog
        dialogTitle="Create Rates"
        open={dialogOpen}
        dialogClose={onClose}
        closeIcon={true}
        maxWidth="sm"
      >
        <DialogContent>
          <Formik
            initialValues={{
              rateType: "",
              title: "",
              displayName: displayName,
              amount: 0,
              minimumAmount: 0,
              endDay: null,
              endTime: null,
              gracePeriodEndTime: null,
              endTimeObj: null,
              gracePeriodEndTimeObj: null,
              startDay: null,
              startTime: null,
              startTimeObj: null,
              hours: 0,
              gracePeriod: 0,
              gracePeriodUnit: "minutes",
              isValidationCodeRequired: false,
              secondStepValidation: false,
              payNowValidationLaterFlow: false,
              timeType: "",
              hourUnit: "",
              isRateOption: false,
              isFreeRate: false,
              isPermit: false,
              isPass: false,
              isCustomSubscription: false,
              isFreeSubscription: false,
              rates: [],
              rateArray: [],
              customStartDate: null,
              customEndDate: null,
              freeParkingLimit: 1,
              freeParkingResetTime: 0,
              groupedPlaceIds: [placeId],
              isGroupedLocation,
              gracePeriodType: "minuteBased",
            }}
            validationSchema={createRateValidation}
            onSubmit={(value, action) => {
              console.log("values", value);
              if (value.timeType === "Custom Duration") {
                value["customStartDate"] = moment(
                  value["customStartDate"],
                ).format("YYYY-MM-DDT00:00:00.000[Z]");
                value["customEndDate"] = moment(value["customEndDate"]).format(
                  "YYYY-MM-DDT23:59:59.999[Z]",
                );
              }
              handleCreateRate({ ...value, placeId });
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <Grid container spacing={2} className="mt-1">
                  <Grid item xs={12} md={12} lg={12}>
                    <FormControl fullWidth>
                      <Autocomplete
                        disableClearable
                        name="rateType"
                        value={props.values.rateType}
                        options={[
                          "Hourly",
                          "Daily",
                          "All Day",
                          "Overnight",
                          "Custom",
                          "Monthly",
                        ]}
                        onChange={(event, newValue) => {
                          props.setValues({
                            ...props?.initialValues,
                            rateType: newValue,
                          });
                        }}
                        onBlur={props.handleBlur}
                        error={
                          props.errors.rateType && props.touched.rateType
                            ? true
                            : false
                        }
                        success={
                          props.errors.rateType && props.touched.rateType
                            ? false
                            : true
                        }
                        helperText={
                          props.errors.rateType && props.touched.rateType
                            ? props.errors.rateType
                            : null
                        }
                        freeSolo
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="contained"
                              label={option}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <MDInput
                            label="Rate type"
                            fullWidth={true}
                            {...params}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {[
                    "Hourly",
                    "Daily",
                    "All Day",
                    "Overnight",
                    "Custom",
                    "Weekly",
                    "Monthly",
                  ].includes(props.values.rateType) ? (
                    <>
                      {isGroupedLocation && (
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <Autocomplete
                              multiple
                              disableCloseOnSelect
                              options={placeList || []}
                              value={placeList.filter((p) =>
                                props.values.groupedPlaceIds.includes(p._id),
                              )}
                              getOptionLabel={(option) =>
                                `${option?.parkingCode} - ${option?.google?.formatted_address}`
                              }
                              onChange={(e, value) => {
                                const selectedIds = value.map(
                                  (item) => item._id,
                                );

                                if (!selectedIds.includes(placeId)) {
                                  selectedIds.unshift(placeId);
                                }

                                props.setFieldValue(
                                  "groupedPlaceIds",
                                  selectedIds,
                                );
                              }}
                              renderOption={(
                                optionProps,
                                option,
                                { selected },
                              ) => (
                                <li {...optionProps}>
                                  <Checkbox
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                    disabled={option._id === placeId}
                                  />
                                  {`${option.parkingCode} - ${option.google?.formatted_address}`}
                                </li>
                              )}
                              renderInput={(params) => (
                                <MDInput
                                  {...params}
                                  label="Select Grouped Places"
                                  placeholder="Choose places"
                                  error={
                                    props.errors.groupedPlaceIds &&
                                    props.touched.groupedPlaceIds
                                  }
                                  helperText={
                                    props.errors.groupedPlaceIds &&
                                    props.touched.groupedPlaceIds
                                      ? props.errors.groupedPlaceIds
                                      : null
                                  }
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>
                      )}

                      <Grid item xs={12} md={12} lg={12}>
                        <FormControl fullWidth>
                          <MDInput
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            name="title"
                            value={props.values?.title}
                            label="Title"
                            onBlur={props.handleBlur}
                            onChange={props.handleChange}
                            error={
                              props.errors.title && props.touched.title
                                ? true
                                : false
                            }
                            success={
                              props.errors.title && props.touched.title
                                ? false
                                : true
                            }
                            helperText={
                              props.errors.title && props.touched.title
                                ? props.errors.title
                                : null
                            }
                          />
                        </FormControl>
                        {(props.values.rateType === "Custom" ||
                          props.values.rateType === "Monthly") && (
                          <FormControl fullWidth className="mt-3">
                            <MDInput
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              name="displayName"
                              value={displayName}
                              label="Display Name"
                              onBlur={props.handleBlur}
                              onChange={(e) => {
                                props.handleChange(e);
                                if (
                                  props.values.rateType === "Custom" ||
                                  props.values.rateType === "Monthly"
                                ) {
                                  setDisplayName(e.target.value);
                                }
                              }}
                              disabled={
                                props.values.rateType !== "Custom" &&
                                props.values.rateType !== "Monthly"
                                  ? true
                                  : false
                              }
                              error={
                                props.errors.displayName &&
                                props.touched.displayName
                                  ? true
                                  : false
                              }
                              success={
                                props.errors.displayName &&
                                props.touched.displayName
                                  ? false
                                  : true
                              }
                              helperText={
                                props.errors.displayName &&
                                props.touched.displayName
                                  ? props.errors.displayName
                                  : null
                              }
                            />
                          </FormControl>
                        )}

                        {props.values.isRateOption ? (
                          <>
                            <FormControl fullWidth className="mt-3">
                              <Autocomplete
                                multiple
                                disableClearable
                                name="rateId"
                                value={props.values?.rateArray || []}
                                options={ratesData}
                                onChange={(event, newValues) => {
                                  props.setValues({
                                    ...props.values,
                                    rates: newValues?.map((rate) => ({
                                      _id: rate._id,
                                      displayName: rate.displayName,
                                      rateType: rate.rateType,
                                      amount: rate.amount,
                                    })),
                                    rateArray: newValues,
                                  });
                                }}
                                onBlur={props.handleBlur}
                                getOptionLabel={(option) =>
                                  `${option.displayName} ${
                                    option.title ? `(${option.title})` : ""
                                  }`
                                }
                                renderInput={(params) => (
                                  <MDInput label="Rate" fullWidth {...params} />
                                )}
                                renderTags={(value, getTagProps) =>
                                  value.map((option, index) => (
                                    <Chip
                                      key={index}
                                      variant="contained"
                                      label={option.displayName}
                                      {...getTagProps({ index })}
                                    />
                                  ))
                                }
                              />
                            </FormControl>
                          </>
                        ) : !props.values.isFreeRate &&
                          !props.values.isPermit &&
                          !props.values.isFreeSubscription ? (
                          <>
                            <FormControl fullWidth className="mt-3">
                              <MDInput
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                name="amount"
                                value={props.values?.amount ?? ""}
                                label="Amount"
                                type="number"
                                onBlur={props.handleBlur}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  const parsedValue = parseFloat(value);
                                  props.setFieldValue("amount", parsedValue);
                                }}
                                error={
                                  props.errors.amount && props.touched.amount
                                    ? true
                                    : false
                                }
                                success={
                                  props.errors.amount && props.touched.amount
                                    ? false
                                    : true
                                }
                                helperText={
                                  props.errors.amount && props.touched.amount
                                    ? props.errors.amount
                                    : null
                                }
                              />
                            </FormControl>
                            {["Weekly", "Monthly"].includes(
                              props.values.rateType,
                            ) &&
                              !props.values.isCustomSubscription && (
                                <FormControl fullWidth className="mt-3">
                                  <MDInput
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    name="minimumAmount"
                                    value={props.values?.minimumAmount ?? ""}
                                    label={
                                      <span
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "6px",
                                        }}
                                      >
                                        Minimum Amount
                                        <CustomTooltip
                                          id="minimumAmount"
                                          title={tabInfo.minimumAmount}
                                          openTooltipId={openTooltipId}
                                          setOpenTooltipId={setOpenTooltipId}
                                          iconSize={18}
                                        />
                                      </span>
                                    }
                                    type="number"
                                    onBlur={props.handleBlur}
                                    onChange={(e) => {
                                      const { value } = e.target;
                                      const parsedValue = parseFloat(value);
                                      props.setFieldValue(
                                        "minimumAmount",
                                        parsedValue,
                                      );
                                    }}
                                    error={
                                      props.errors.minimumAmount &&
                                      props.touched.minimumAmount
                                        ? true
                                        : false
                                    }
                                    success={
                                      props.errors.minimumAmount &&
                                      props.touched.minimumAmount
                                        ? false
                                        : true
                                    }
                                    helperText={
                                      props.errors.minimumAmount &&
                                      props.touched.minimumAmount
                                        ? props.errors.minimumAmount
                                        : null
                                    }
                                  />
                                </FormControl>
                              )}
                          </>
                        ) : null}
                      </Grid>
                    </>
                  ) : null}

                  {props.values.rateType === "Daily" ? (
                    <Grid item xs={12} md={12} lg={12}>
                      <Autocomplete
                        options={generateNumbers(1, 24)}
                        getOptionLabel={(option) => option.label ?? option}
                        name="hours"
                        value={props.values.hours}
                        isOptionEqualToValue={(option, value) => {
                          if (value === "" || option.label === value) {
                            return true;
                          }
                        }}
                        onChange={(event, newValue) => {
                          const parsedValue = parseFloat(newValue.label);
                          props.setFieldValue(
                            "hours",
                            !isNaN(parsedValue) ? parsedValue : null,
                          );
                        }}
                        onBlur={props.handleBlur}
                        autoHighlight
                        disableClearable
                        size="medium"
                        renderInput={(params) => (
                          <MDInput
                            label="Hours"
                            fullWidth={true}
                            inputProps={{
                              ...params.inputProps,
                              autoComplete: "new-password",
                            }}
                            error={
                              props.errors.hours && props.touched.hours
                                ? true
                                : false
                            }
                            helperText={
                              props.errors.hours && props.touched.hours
                                ? props.errors.hours
                                : null
                            }
                            {...params}
                          />
                        )}
                      />
                    </Grid>
                  ) : props.values.rateType === "Overnight" ? (
                    <>
                      <Grid item xs={12} md={12} lg={12}>
                        <Autocomplete
                          options={endDayOptions[props.values?.rateType]}
                          getOptionLabel={(option) => option.label ?? option}
                          name="endDay"
                          value={props.values.endDay}
                          isOptionEqualToValue={(option, value) => {
                            if (value === "" || option.label === value) {
                              return true;
                            }
                          }}
                          onChange={(event, newValue) => {
                            props.setFieldValue("endDay", newValue.label);
                          }}
                          onBlur={props.handleBlur}
                          autoHighlight
                          disableClearable
                          size="medium"
                          renderInput={(params) => (
                            <MDInput
                              label="End Day"
                              fullWidth={true}
                              inputProps={{
                                ...params.inputProps,
                                autoComplete: "new-password",
                              }}
                              error={
                                props.errors.endDay && props.touched.endDay
                                  ? true
                                  : false
                              }
                              helperText={
                                props.errors.endDay && props.touched.endDay
                                  ? props.errors.endDay
                                  : null
                              }
                              {...params}
                            />
                          )}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        onClick={() => setEndDayTimePickerOpen(true)}
                      >
                        <DesktopTimePicker
                          name="endTimeObj"
                          value={props.values.endTimeObj}
                          onChange={(newTime) => {
                            props.setFieldValue("endTimeObj", newTime);
                            props.setFieldValue(
                              "endTime",
                              moment(newTime)?.format("HH:mm"),
                            );
                          }}
                          onBlur={props.handleBlur}
                          open={isEndDayTimePickerOpen}
                          onClose={() => setEndDayTimePickerOpen(false)}
                          timeSteps={{ minutes: 1 }}
                          sx={{ width: "100%", mb: 1 }}
                          label="End Day Time"
                        />
                      </Grid>
                    </>
                  ) : null}

                  {props.values.rateType === "All Day" ? (
                    <Grid
                      item
                      xs={12}
                      md={12}
                      lg={12}
                      onClick={() => setEndDayTimePickerOpen(true)}
                    >
                      <DesktopTimePicker
                        name="endTimeObj"
                        value={props.values.endTimeObj}
                        onChange={(newTime) => {
                          props.setFieldValue("endTimeObj", newTime);
                          props.setFieldValue(
                            "endTime",
                            moment(newTime)?.format("HH:mm"),
                          );
                        }}
                        onBlur={props.handleBlur}
                        open={isEndDayTimePickerOpen}
                        onClose={() => setEndDayTimePickerOpen(false)}
                        timeSteps={{ minutes: 1 }}
                        sx={{ width: "100%", mb: 1 }}
                        label="End Day Time"
                      />
                    </Grid>
                  ) : null}

                  {(props.values.secondStepValidation ||
                    props.values.payNowValidationLaterFlow) && (
                    <>
                      <Grid item xs={12}>
                        <RadioGroup
                          name="gracePeriodType"
                          sx={{ flexDirection: "row" }}
                          value={props.values.gracePeriodType}
                          onChange={(e) =>
                            props.setFieldValue(
                              "gracePeriodType",
                              e.target.value,
                            )
                          }
                        >
                          <FormControlLabel
                            value="minuteBased"
                            control={<Radio color="primary" />}
                            label="Minute Based"
                          />
                          <FormControlLabel
                            value="fixedEndTime"
                            control={<Radio />}
                            label="Fixed end time"
                          />
                        </RadioGroup>
                      </Grid>

                      {props.values.gracePeriodType === "minuteBased" ? (
                        <Grid item xs={12} md={12} lg={12}>
                          <Autocomplete
                            options={gracePeriodOptions}
                            getOptionLabel={(option) => option.label ?? option}
                            name="gracePeriod"
                            value={props.values.gracePeriod}
                            isOptionEqualToValue={(option, value) => {
                              if (value === "" || option.label === value) {
                                return true;
                              }
                            }}
                            onChange={(event, newValue) => {
                              const parsedValue = parseFloat(newValue.label);
                              props.setFieldValue(
                                "gracePeriod",
                                !isNaN(parsedValue) ? parsedValue : null,
                              );
                            }}
                            onBlur={props.handleBlur}
                            autoHighlight
                            disableClearable
                            size="medium"
                            renderInput={(params) => (
                              <MDInput
                                label="Validation Grace Period (Minutes)"
                                fullWidth={true}
                                inputProps={{
                                  ...params.inputProps,
                                  autoComplete: "new-password",
                                }}
                                error={
                                  props.errors.gracePeriod &&
                                  props.touched.gracePeriod
                                    ? true
                                    : false
                                }
                                helperText={
                                  props.errors.gracePeriod &&
                                  props.touched.gracePeriod
                                    ? props.errors.gracePeriod
                                    : null
                                }
                                {...params}
                              />
                            )}
                          />
                        </Grid>
                      ) : (
                        <Grid
                          item
                          xs={12}
                          md={12}
                          lg={12}
                          onClick={() => setGraceEndDayTimePickerOpen(true)}
                        >
                          <DesktopTimePicker
                            name="gracePeriodEndTimeObj"
                            value={props.values.gracePeriodEndTimeObj}
                            onChange={(newTime) => {
                              props.setFieldValue(
                                "gracePeriodEndTimeObj",
                                newTime,
                              );
                              props.setFieldValue(
                                "gracePeriodEndTime",
                                moment(newTime)?.format("HH:mm"),
                              );
                            }}
                            onBlur={props.handleBlur}
                            open={isGraceEndDayTimePickerOpen}
                            onClose={() => setGraceEndDayTimePickerOpen(false)}
                            timeSteps={{ minutes: 1 }}
                            sx={{ width: "100%", mb: 1 }}
                            label="Grace Period End Day Time"
                          />
                        </Grid>
                      )}
                    </>
                  )}

                  {props.values.rateType === "Custom" && (
                    <>
                      {!props.values.isRateOption && !props.values.isPermit && (
                        <>
                          <Grid item xs={12} md={12} lg={12}>
                            <FormControl fullWidth>
                              <InputLabel id="demo-simple-select-label">
                                Time Type
                              </InputLabel>
                              <MDDropDown
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                name="timeType"
                                value={props.values.timeType}
                                label="Time Type"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                              >
                                <MenuItem value={"Hour Based"}>
                                  Hour Based
                                </MenuItem>
                                <MenuItem value={"Fixed End Time"}>
                                  Fixed End Time
                                </MenuItem>
                                <MenuItem value={"Custom Duration"}>
                                  Custom Duration
                                </MenuItem>
                              </MDDropDown>
                            </FormControl>
                          </Grid>

                          {props.values.isPass && (
                            <Grid
                              item
                              xs={12}
                              md={12}
                              lg={12}
                              onClick={() => setStartDayTimePickerOpen(true)}
                            >
                              <DesktopTimePicker
                                name="startTimeObj"
                                value={props.values.startTimeObj}
                                onChange={(newTime) => {
                                  props.setFieldValue("startTimeObj", newTime);
                                  props.setFieldValue(
                                    "startTime",
                                    moment(newTime)?.format("HH:mm"),
                                  );
                                }}
                                onBlur={props.handleBlur}
                                open={isStartDayTimePickerOpen}
                                onClose={() => setStartDayTimePickerOpen(false)}
                                timeSteps={{ minutes: 1 }}
                                sx={{ width: "100%", mb: 1 }}
                                label="Start Day Time"
                              />
                            </Grid>
                          )}

                          {props.values.isPass && (
                            <Grid item xs={12} md={12} lg={12}>
                              <Autocomplete
                                options={startDayOptions}
                                getOptionLabel={(option) =>
                                  option.label ?? option
                                }
                                name="startDay"
                                value={props.values.startDay}
                                isOptionEqualToValue={(option, value) => {
                                  if (value === "" || option.label === value) {
                                    return true;
                                  }
                                }}
                                onChange={(event, newValue) => {
                                  props.setFieldValue(
                                    "startDay",
                                    newValue.label,
                                  );
                                }}
                                onBlur={props.handleBlur}
                                autoHighlight
                                disableClearable
                                size="medium"
                                renderInput={(params) => (
                                  <MDInput
                                    label="Start Day"
                                    fullWidth={true}
                                    inputProps={{
                                      ...params.inputProps,
                                      autoComplete: "new-password",
                                    }}
                                    error={
                                      props.errors.startDay &&
                                      props.touched.startDay
                                        ? true
                                        : false
                                    }
                                    helperText={
                                      props.errors.startDay &&
                                      props.touched.startDay
                                        ? props.errors.startDay
                                        : null
                                    }
                                    {...params}
                                  />
                                )}
                              />
                            </Grid>
                          )}

                          {props.values.timeType === "Hour Based" ? (
                            <Grid item xs={12} md={12} lg={12}>
                              <Autocomplete
                                options={hoursOptions}
                                getOptionLabel={(option) =>
                                  option.label ?? option
                                }
                                name="hours"
                                value={props.values.hours}
                                isOptionEqualToValue={(option, value) => {
                                  if (value === "" || option.label === value) {
                                    return true;
                                  }
                                }}
                                onChange={(event, newValue) => {
                                  const isMinValue =
                                    newValue.label.includes("MINUTE");
                                  props.setFieldValue(
                                    "hourUnit",
                                    isMinValue ? "MINUTES" : "HOURS",
                                  );
                                  const parsedValue = parseFloat(
                                    newValue.label,
                                  );
                                  props.setFieldValue(
                                    "hours",
                                    !isNaN(parsedValue) ? parsedValue : null,
                                  );
                                }}
                                onBlur={props.handleBlur}
                                autoHighlight
                                disableClearable
                                size="medium"
                                renderInput={(params) => (
                                  <MDInput
                                    label="Hours"
                                    fullWidth={true}
                                    inputProps={{
                                      ...params.inputProps,
                                      autoComplete: "new-password",
                                    }}
                                    error={
                                      props.errors.hours && props.touched.hours
                                        ? true
                                        : false
                                    }
                                    helperText={
                                      props.errors.hours && props.touched.hours
                                        ? props.errors.hours
                                        : null
                                    }
                                    {...params}
                                  />
                                )}
                              />
                            </Grid>
                          ) : props.values.timeType === "Custom Duration" ? (
                            <Grid item xs={12} md={12} lg={12}>
                              <FormControl fullWidth>
                                <DateRangePicker
                                  id="dateDuration"
                                  placeholder="Rate Start Date - Rate End Date"
                                  character=" - "
                                  size="lg"
                                  format="dd-MM-yyyy"
                                  showMeridian
                                  block
                                  placement="top"
                                  value={
                                    props.values.customStartDate &&
                                    props.values.customEndDate
                                      ? [
                                          new Date(
                                            props.values.customStartDate,
                                          ),
                                          new Date(props.values.customEndDate),
                                        ]
                                      : undefined
                                  }
                                  ranges={[]}
                                  onChange={(value) => {
                                    if (value && value.length === 2) {
                                      props.setFieldValue(
                                        "customStartDate",
                                        moment(value[0]).format(),
                                      );
                                      props.setFieldValue(
                                        "customEndDate",
                                        moment(value[1]).format(),
                                      );
                                    } else {
                                      props.setFieldValue(
                                        "customStartDate",
                                        null,
                                      );
                                      props.setFieldValue(
                                        "customEndDate",
                                        null,
                                      );
                                    }
                                  }}
                                  onBlur={props.handleBlur}
                                />
                              </FormControl>
                            </Grid>
                          ) : (
                            <>
                              <Grid
                                item
                                xs={12}
                                md={12}
                                lg={12}
                                onClick={() => setEndDayTimePickerOpen(true)}
                              >
                                <DesktopTimePicker
                                  name="endTimeObj"
                                  value={props.values.endTimeObj}
                                  onChange={(newTime) => {
                                    props.setFieldValue("endTimeObj", newTime);
                                    props.setFieldValue(
                                      "endTime",
                                      moment(newTime)?.format("HH:mm"),
                                    );
                                  }}
                                  onBlur={props.handleBlur}
                                  open={isEndDayTimePickerOpen}
                                  onClose={() => setEndDayTimePickerOpen(false)}
                                  timeSteps={{ minutes: 1 }}
                                  sx={{ width: "100%", mb: 1 }}
                                  label="End Day Time"
                                />
                              </Grid>
                              <Grid item xs={12} md={12} lg={12}>
                                <Autocomplete
                                  options={
                                    endDayOptions[props.values?.rateType]
                                  }
                                  getOptionLabel={(option) =>
                                    option.label ?? option
                                  }
                                  name="endDay"
                                  value={props.values.endDay}
                                  isOptionEqualToValue={(option, value) => {
                                    if (
                                      value === "" ||
                                      option.label === value
                                    ) {
                                      return true;
                                    }
                                  }}
                                  onChange={(event, newValue) => {
                                    props.setFieldValue(
                                      "endDay",
                                      newValue.label,
                                    );
                                  }}
                                  onBlur={props.handleBlur}
                                  autoHighlight
                                  disableClearable
                                  size="medium"
                                  renderInput={(params) => (
                                    <MDInput
                                      label="End Day"
                                      fullWidth={true}
                                      inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "new-password",
                                      }}
                                      error={
                                        props.errors.endDay &&
                                        props.touched.endDay
                                          ? true
                                          : false
                                      }
                                      helperText={
                                        props.errors.endDay &&
                                        props.touched.endDay
                                          ? props.errors.endDay
                                          : null
                                      }
                                      {...params}
                                    />
                                  )}
                                />
                              </Grid>
                            </>
                          )}
                        </>
                      )}
                      {!props.values.isFreeRate &&
                        !props.values.isPass &&
                        !props.values.secondStepValidation &&
                        !props.values.isPermit && (
                          <Grid item xs={12} md={6} lg={6}>
                            <MDBox display="flex" alignItems="center" ml={-1}>
                              <Checkbox
                                checked={props.values.isRateOption}
                                onChange={(e) => {
                                  props.handleChange(e);

                                  props.setFieldValue(
                                    "isRateOption",
                                    e.target.checked,
                                  );
                                  if (e.target.checked) {
                                    props.setFieldValue(
                                      "isValidationCodeRequired",
                                      false,
                                    );
                                  }
                                }}
                                inputProps={{
                                  "aria-label": "controlled",
                                }}
                              />
                              <MDTypography
                                variant="button"
                                fontWeight="regular"
                                color="text"
                              >
                                &nbsp;&nbsp;Create as Parent Rate&nbsp;
                              </MDTypography>
                              <CustomTooltip
                                id="parentRate"
                                title={tabInfo.parentRate}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                                iconSize={18}
                              />
                            </MDBox>
                          </Grid>
                        )}

                      {props.values.isFreeRate && (
                        <>
                          <Grid item xs={12}>
                            <Autocomplete
                              options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                              getOptionLabel={(option) => option}
                              name="freeParkingLimit"
                              value={props.values.freeParkingLimit}
                              isOptionEqualToValue={(option, value) => {
                                if (value === "" || option === value) {
                                  return true;
                                }
                              }}
                              onChange={(event, newValue) => {
                                const parsedValue = parseFloat(newValue);
                                props.setFieldValue(
                                  "freeParkingLimit",
                                  !isNaN(parsedValue) ? parsedValue : null,
                                );
                              }}
                              onBlur={props.handleBlur}
                              autoHighlight
                              disableClearable
                              size="medium"
                              renderInput={(params) => (
                                <MDInput
                                  label="Free Parking Limit (Per License Plate)"
                                  fullWidth={true}
                                  inputProps={{
                                    ...params.inputProps,
                                    autoComplete: "new-password",
                                  }}
                                  error={
                                    props.errors.freeParkingLimit &&
                                    props.touched.freeParkingLimit
                                      ? true
                                      : false
                                  }
                                  helperText={
                                    props.errors.freeParkingLimit &&
                                    props.touched.freeParkingLimit
                                      ? props.errors.freeParkingLimit
                                      : null
                                  }
                                  {...params}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Autocomplete
                              options={hoursOptionsForFreeParking}
                              getOptionLabel={(option) =>
                                option.label ?? option
                              }
                              name="freeParkingResetTime"
                              value={props.values.freeParkingResetTime}
                              isOptionEqualToValue={(option, value) => {
                                if (value === "" || option.label === value) {
                                  return true;
                                }
                              }}
                              onChange={(event, newValue) => {
                                const parsedValue = parseFloat(newValue.label);
                                props.setFieldValue(
                                  "freeParkingResetTime",
                                  !isNaN(parsedValue) ? parsedValue : null,
                                );
                              }}
                              onBlur={props.handleBlur}
                              autoHighlight
                              disableClearable
                              size="medium"
                              renderInput={(params) => (
                                <MDInput
                                  label="Free Parking Rest Time (In Hour)"
                                  fullWidth={true}
                                  inputProps={{
                                    ...params.inputProps,
                                    autoComplete: "new-password",
                                  }}
                                  error={
                                    props.errors.freeParkingResetTime &&
                                    props.touched.freeParkingResetTime
                                      ? true
                                      : false
                                  }
                                  helperText={
                                    props.errors.freeParkingResetTime &&
                                    props.touched.freeParkingResetTime
                                      ? props.errors.freeParkingResetTime
                                      : null
                                  }
                                  {...params}
                                />
                              )}
                            />
                          </Grid>
                        </>
                      )}

                      {!props.values.isRateOption &&
                        !props.values.secondStepValidation &&
                        !props.values.isPass &&
                        !props.values.isPermit && (
                          <Grid item xs={12} md={6} lg={6}>
                            <MDBox display="flex" alignItems="center" ml={-1}>
                              <Checkbox
                                checked={props.values.isFreeRate}
                                onChange={(e) => {
                                  props.handleChange(e);

                                  props.setFieldValue(
                                    "isFreeRate",
                                    e.target.checked,
                                  );
                                }}
                                inputProps={{ "aria-label": "controlled" }}
                              />
                              <MDTypography
                                variant="button"
                                fontWeight="regular"
                                color="text"
                              >
                                &nbsp;&nbsp;Create Free Rate&nbsp;
                              </MDTypography>
                              <CustomTooltip
                                id="freeRate"
                                title={tabInfo.freeRate}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                                iconSize={18}
                              />
                            </MDBox>
                          </Grid>
                        )}
                      {props.values.isPermit && (
                        <>
                          <Grid item xs={4}>
                            <Autocomplete
                              disableClearable
                              options={gracePeriodTypeOptions}
                              value={
                                gracePeriodTypeOptions.find(
                                  (item) =>
                                    item.value === props.values.gracePeriodUnit,
                                ) || gracePeriodTypeOptions[0]
                              }
                              getOptionLabel={(option) => option.label}
                              onChange={(e, newValue) => {
                                props.setFieldValue(
                                  "gracePeriodUnit",
                                  newValue.value,
                                );
                                props.setFieldValue("gracePeriod", 0);
                              }}
                              renderInput={(params) => (
                                <MDInput
                                  {...params}
                                  label="Grace period unit"
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={8}>
                            <Autocomplete
                              disableClearable
                              options={
                                permitGracePeriodOptions[
                                  props.values.gracePeriodUnit
                                ]
                              }
                              value={
                                permitGracePeriodOptions[
                                  props.values.gracePeriodUnit
                                ].find(
                                  (item) =>
                                    item.value === props.values.gracePeriod,
                                ) || null
                              }
                              getOptionLabel={(option) => option.label}
                              onChange={(e, newValue) => {
                                props.setFieldValue(
                                  "gracePeriod",
                                  newValue.value,
                                );
                              }}
                              renderInput={(params) => (
                                <MDInput {...params} label="Grace Period" />
                              )}
                            />
                          </Grid>
                        </>
                      )}
                      {!props.values.isFreeRate &&
                        !props.values.isRateOption &&
                        !props.values.secondStepValidation &&
                        !props.values.secondStepValidation &&
                        !props.values.isValidationCodeRequired &&
                        !props.values.payNowValidationLaterFlow &&
                        !props.values.isPass && (
                          <Grid item xs={12} md={6} lg={6}>
                            <MDBox display="flex" alignItems="center" ml={-1}>
                              <Checkbox
                                checked={props.values.isPermit}
                                onChange={(e) => {
                                  props.handleChange(e);

                                  props.setFieldValue(
                                    "isPermit",
                                    e.target.checked,
                                  );
                                }}
                                inputProps={{
                                  "aria-label": "controlled",
                                }}
                              />
                              <MDTypography
                                variant="button"
                                fontWeight="regular"
                                color="text"
                              >
                                &nbsp;&nbsp;Create Permit Rate&nbsp;
                              </MDTypography>
                              <CustomTooltip
                                id="permitRate"
                                title={tabInfo.permitRate}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                                iconSize={18}
                              />
                            </MDBox>
                          </Grid>
                        )}

                      {!props.values.isFreeRate &&
                        !props.values.isPermit &&
                        placeData?.brandId?.isPassEnable && (
                          <Grid item xs={12} md={12} lg={12}>
                            <MDBox display="flex" alignItems="center" ml={-1}>
                              <Checkbox
                                checked={props.values.isPass}
                                onChange={(e) => {
                                  props.handleChange(e);

                                  props.setFieldValue(
                                    "isPass",
                                    e.target.checked,
                                  );
                                }}
                                inputProps={{
                                  "aria-label": "controlled",
                                }}
                              />
                              <MDTypography
                                variant="button"
                                fontWeight="regular"
                                color="text"
                              >
                                &nbsp;&nbsp;Create Shuttle Pass Rate&nbsp;
                              </MDTypography>
                              <CustomTooltip
                                id="isPass"
                                title={tabInfo.isPass}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                                iconSize={18}
                              />
                            </MDBox>
                          </Grid>
                        )}
                    </>
                  )}

                  {props.values.rateType !== "" &&
                    !props.values.isFreeRate &&
                    !props.values.isRateOption &&
                    props.values.rateType !== "Monthly" &&
                    !props.values.isPass &&
                    !props.values.isPermit && (
                      <>
                        {!props.values.payNowValidationLaterFlow && (
                          <Grid item xs={12} md={6} lg={6}>
                            <MDBox display="flex" alignItems="center" ml={-1}>
                              <Checkbox
                                checked={props.values.secondStepValidation}
                                onChange={(e) => {
                                  props.handleChange(e);
                                  props.setFieldValue(
                                    "secondStepValidation",
                                    e.target.checked,
                                  );
                                  if (e.target.checked) {
                                    props.setFieldValue(
                                      "isValidationCodeRequired",
                                      false,
                                    );
                                  }
                                }}
                                inputProps={{ "aria-label": "controlled" }}
                              />

                              <MDTypography
                                variant="button"
                                fontWeight="regular"
                                color="text"
                              >
                                &nbsp;&nbsp;Create as Validate Later Rate&nbsp;
                              </MDTypography>
                              <CustomTooltip
                                id="createRateValidateLater"
                                title={tabInfo.createRateValidateLater}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                                iconSize={18}
                              />
                            </MDBox>
                          </Grid>
                        )}

                        {!props.values.secondStepValidation &&
                          !props.values.isPass && (
                            <Grid item xs={12} md={6} lg={6}>
                              <MDBox display="flex" alignItems="center" ml={-1}>
                                <Checkbox
                                  checked={
                                    props.values.payNowValidationLaterFlow
                                  }
                                  onChange={(e) => {
                                    props.handleChange(e);
                                    props.setFieldValue(
                                      "payNowValidationLaterFlow",
                                      e.target.checked,
                                    );
                                    if (e.target.checked) {
                                      props.setFieldValue(
                                        "isValidationCodeRequired",
                                        false,
                                      );
                                    }
                                  }}
                                  inputProps={{ "aria-label": "controlled" }}
                                />
                                <MDTypography
                                  variant="button"
                                  fontWeight="regular"
                                  color="text"
                                >
                                  &nbsp;&nbsp;Create as Pay Now Validate
                                  &nbsp;&nbsp;Later Rate&nbsp;
                                </MDTypography>
                                <CustomTooltip
                                  id="payNowValidateLater"
                                  title={tabInfo.payNowValidateLater}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconSize={18}
                                />
                              </MDBox>
                            </Grid>
                          )}
                        {!props.values.payNowValidationLaterFlow &&
                          !props.values.isPass &&
                          !props.values.secondStepValidation && (
                            <Grid item xs={12} md={6} lg={6}>
                              <MDBox display="flex" alignItems="center" ml={-1}>
                                <Checkbox
                                  checked={
                                    props.values.isValidationCodeRequired
                                  }
                                  onChange={(e) => {
                                    props.handleChange(e);
                                    props.setFieldValue(
                                      "isValidationCodeRequired",
                                      e.target.checked,
                                    );
                                  }}
                                  inputProps={{ "aria-label": "controlled" }}
                                />
                                <MDTypography
                                  variant="button"
                                  fontWeight="regular"
                                  color="text"
                                >
                                  &nbsp;&nbsp;Require Validation Code&nbsp;
                                </MDTypography>
                                <CustomTooltip
                                  id="validationCode"
                                  title={tabInfo.validationCode}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconSize={18}
                                />
                              </MDBox>
                            </Grid>
                          )}
                      </>
                    )}
                  {props.values.rateType !== "" &&
                    props.values.rateType === "Monthly" && (
                      <>
                        <Grid item xs={12} md={12} lg={12}>
                          <MDBox display="flex" alignItems="center" ml={-1}>
                            <Checkbox
                              checked={props.values.isCustomSubscription}
                              onChange={(e) => {
                                props.handleChange(e);
                                props.setFieldValue(
                                  "isCustomSubscription",
                                  e.target.checked,
                                );
                              }}
                              inputProps={{ "aria-label": "controlled" }}
                            />
                            <MDTypography
                              variant="button"
                              fontWeight="regular"
                              color="text"
                            >
                              &nbsp;&nbsp;Create as 30 Day Rolling Monthly Rate
                              &nbsp;
                            </MDTypography>
                            <CustomTooltip
                              id="isCustomSubscription"
                              title={tabInfo.isCustomSubscription}
                              openTooltipId={openTooltipId}
                              setOpenTooltipId={setOpenTooltipId}
                              iconSize={18}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={12} lg={12}>
                          <MDBox display="flex" alignItems="center" ml={-1}>
                            <Checkbox
                              checked={props.values.isFreeSubscription}
                              onChange={(e) => {
                                props.handleChange(e);
                                props.setFieldValue(
                                  "isFreeSubscription",
                                  e.target.checked,
                                );
                                if (e.target.checked) {
                                  props.setFieldValue("amount", 0);
                                  props.setFieldValue("minimumAmount", 0);
                                }
                              }}
                              inputProps={{ "aria-label": "controlled" }}
                            />
                            <MDTypography
                              variant="button"
                              fontWeight="regular"
                              color="text"
                            >
                              &nbsp;&nbsp;Free Subscription &nbsp;
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      </>
                    )}

                  <Grid item xs={12} className="text-right">
                    <MDButton
                      color={sidenavColor}
                      variant="contained"
                      type="submit"
                    >
                      Create
                    </MDButton>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </MDDialog>
    </>
  );
};

export default CreateRates;
